/**
 * AgendamentoTab - Aba de Agendamento da Contagem
 */
import React from 'react';
import { Check, X } from 'lucide-react';
import { DatePicker } from '../DatePicker';
import { TimePicker } from '../TimePicker';
import { MultiSelectFilter } from '../MultiSelectFilter';
import { ComboboxSelector } from '../ui/ComboboxSelector';
import { AgendamentoLocaisSection } from './AgendamentoLocaisSection';
import { AgendamentoLocaisPorClienteSection } from './AgendamentoLocaisPorClienteSection';
import { AgendamentoAuditoresSection } from './AgendamentoAuditoresSection';
import { HospitalContatosSection } from './HospitalContatosSection';  // N75 + N103
import type { UseContagemLocaisPorClienteReturn } from '../../hooks/useContagemLocaisPorCliente';
import { STATUS_MAP_REVERSE } from '../../constants/status';
import type { Estabelecimento, LocalContagem } from '../../types/estabelecimento';
import type { Cliente } from '../../services/clienteService';
import type { User } from '../../services/userService';
import { computeHoraFim59min } from '../../utils/duracao';

interface AgendamentoTabProps {
  formData: any;
  isNewContagem: boolean;
  isReadOnly: boolean;
  isEditMode: boolean;
  modifiedFields: Set<string>;
  user: any;
  statusOptions: string[];
  clientesDisponiveis: Cliente[];
  loadingClientes: boolean;
  selectedClienteIds: number[];
  setSelectedClienteIds: (ids: number[]) => void;
  hospitaisDisponiveis: Estabelecimento[];
  loadingHospitais: boolean;
  selectedHospitalId: number;
  auditoresDisponiveis: User[];
  loadingAuditores: boolean;
  errorAuditores: string | null;
  auditoresParticipantes: User[];
  selectedAuditorId: number;
  setSelectedAuditorId: (id: number) => void;
  locaisContagemDisponiveis: LocalContagem[];
  loadingLocais: boolean;
  locaisContagemSelecionados: LocalContagem[];
  selectedLocalId: number;
  setSelectedLocalId: (id: number) => void;
  handleAddLocal: () => void;
  handleRemoveLocal: () => void;
  // Epic LOCAIS-POR-CLIENTE: new hook for grouped locais by cliente
  locaisPorClienteHook?: UseContagemLocaisPorClienteReturn;
  motivosOptions: any[];
  formatEnvioSaldos: (isoString: string | null) => string;
  handleChange: (field: string, value: string) => void;
  handleSelectHospital: (hospitalId: number) => void;
  handleAddAuditor: () => void;
  handleRemoveAuditor: () => void;
  handleSave: () => void;
  handleCancel?: () => void;
  originalFormData: any;
  setModifiedFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const AgendamentoTab: React.FC<AgendamentoTabProps> = ({
  formData,
  isNewContagem,
  isReadOnly,
  isEditMode,
  modifiedFields,
  user,
  statusOptions,
  clientesDisponiveis,
  loadingClientes,
  selectedClienteIds,
  setSelectedClienteIds,
  hospitaisDisponiveis,
  loadingHospitais,
  selectedHospitalId,
  auditoresDisponiveis,
  loadingAuditores,
  errorAuditores,
  auditoresParticipantes,
  selectedAuditorId,
  setSelectedAuditorId,
  locaisContagemDisponiveis,
  loadingLocais,
  locaisContagemSelecionados,
  selectedLocalId,
  setSelectedLocalId,
  handleAddLocal,
  handleRemoveLocal,
  locaisPorClienteHook,
  motivosOptions,
  formatEnvioSaldos,
  handleChange,
  handleSelectHospital,
  handleAddAuditor,
  handleRemoveAuditor,
  handleSave,
  handleCancel,
  originalFormData,
  setModifiedFields,
}) => {
  const isNegativeStatus = ['NAO_AUTORIZADA', 'NAO_EXECUTADA', 'CANCELADA'].includes(
    STATUS_MAP_REVERSE[formData.status]
  );

  return (
    <div className="p-6 space-y-6">
      {/* Botões para nova contagem */}
      {isNewContagem && (
        <div className="mb-4 flex gap-3">
          <button
            onClick={handleSave}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:from-emerald-600 hover:to-green-700 transition shadow-lg font-semibold"
          >
            <Check size={20} />
            <span>Criar Contagem</span>
          </button>
          {handleCancel && (
            <button
              onClick={handleCancel}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
            >
              <X size={20} />
              <span>Cancelar</span>
            </button>
          )}
        </div>
      )}

      {/* LINHA 0: Data, Hora Inicial, Hora Final e Status */}
      <div className="flex items-end gap-3 mb-4">
        <div className="w-[140px]">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Data</label>
          <DatePicker
            value={formData.dataHora}
            onChange={(data) => handleChange('dataHora', data)}
            disabled={isReadOnly}
            placeholder="📅 Data"
            className={`h-[48px] ${modifiedFields.has('dataHora') ? 'border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50' : ''}`}
          />
        </div>

        <div className="w-[110px]">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Hora Inicial</label>
          <TimePicker
            value={formData.inicio}
            onChange={(hora) => {
              handleChange('inicio', hora);
              // Sugere a Hora Final = inicio + 29min (padrao, util compartilhado).
              // Editavel: o usuario pode sobrescrever no campo Hora Final.
              const fimSugerido = computeHoraFim59min(hora);
              if (fimSugerido) handleChange('termino', fimSugerido);
            }}
            disabled={isReadOnly}
            className={`h-[48px] ${modifiedFields.has('inicio') ? 'border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50' : ''}`}
          />
        </div>

        <div className="w-[110px]">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Hora Final</label>
          <TimePicker
            value={formData.termino}
            onChange={(hora) => handleChange('termino', hora)}
            disabled={isReadOnly}
            className={`h-[48px] ${modifiedFields.has('termino') ? 'border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50' : ''}`}
          />
        </div>

        <div className="w-[200px]">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Envio dos Saldos</label>
          <input
            type="text"
            value={formatEnvioSaldos(formData.envioSaldosCalculado)}
            disabled
            className="w-full h-[48px] px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed opacity-75"
            placeholder="--/--/---- --:--"
          />
        </div>

        <div className="w-[240px]">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Status</label>
          <div className="relative">
            {/* #8: status editavel ao CRIAR (default PRE_AGENDADA) e ao editar como MASTER. */}
            {isNewContagem || (isEditMode && user?.role === 'MASTER') ? (
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className={`w-full h-[48px] px-4 py-2 pr-10 border-2 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  modifiedFields.has('status')
                    ? 'border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                }`}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={formData.status}
                disabled
                className="w-full h-[48px] px-4 py-2 pr-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed opacity-75"
              />
            )}
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg pointer-events-none">📊</span>
          </div>
        </div>
      </div>

      {/* LINHA 1: Cliente e Hospital */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <div className={modifiedFields.has('selectedClienteIds') ? 'ring-2 ring-orange-200 rounded-lg' : ''}>
            <MultiSelectFilter
              label="Clientes *"
              options={[...clientesDisponiveis]
                .sort((a, b) => (a.nome_fantasia || a.razao_social || '').localeCompare(b.nome_fantasia || b.razao_social || '', 'pt-BR', { sensitivity: 'base' }))
                .map(c => ({ value: c.id, label: c.nome_fantasia || c.razao_social }))}
              selectedValues={selectedClienteIds}
              onChange={(values) => {
                const newIds = values.map(Number);
                setSelectedClienteIds(newIds);
                if (isEditMode && originalFormData) {
                  const originalIds = JSON.stringify(originalFormData.selectedClienteIds.sort());
                  const currentIds = JSON.stringify(newIds.sort());
                  setModifiedFields(prev => {
                    const newSet = new Set(prev);
                    if (originalIds !== currentIds) newSet.add('selectedClienteIds');
                    else newSet.delete('selectedClienteIds');
                    return newSet;
                  });
                }
              }}
              placeholder="Selecione Clientes"
              disabled={isReadOnly || loadingClientes}
              showTodos={false}
              showBadges={false}
            />
          </div>
          {!isReadOnly && ['EM_EXECUCAO', 'EXECUTADA', 'NAO_AUTORIZADA', 'NAO_EXECUTADA', 'CANCELADA'].includes(STATUS_MAP_REVERSE[formData.status] || formData.status) && (
            <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
              <span>⚠️</span>
              <span>Clientes não podem ser alterados neste status</span>
            </div>
          )}
          {selectedClienteIds.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedClienteIds.map(clienteId => {
                const cliente = clientesDisponiveis.find(c => c.id === clienteId);
                if (!cliente) return null;
                const nome = cliente.nome_fantasia || cliente.razao_social;
                return (
                  <div
                    key={clienteId}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md font-semibold text-base"
                  >
                    <span className="text-lg">🏢</span>
                    <span>{nome}</span>
                    {!isReadOnly && (
                      <button
                        onClick={() => {
                          const newIds = selectedClienteIds.filter(id => id !== clienteId);
                          setSelectedClienteIds(newIds);
                          if (isEditMode && originalFormData) {
                            const originalIds = JSON.stringify(originalFormData.selectedClienteIds.sort());
                            const currentIds = JSON.stringify(newIds.sort());
                            setModifiedFields(prev => {
                              const newSet = new Set(prev);
                              if (originalIds !== currentIds) newSet.add('selectedClienteIds');
                              else newSet.delete('selectedClienteIds');
                              return newSet;
                            });
                          }
                        }}
                        className="ml-1 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        title="Remover cliente"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <ComboboxSelector
            label="Hospital *"
            options={hospitaisDisponiveis.map(h => ({
              id: h.id,
              label: h.nome_fantasia || h.razao_social,
              subLabel: h.cnpj ? `CNPJ: ${h.cnpj}` : undefined,
              // Story 2.55: incluir versao so-digitos do CNPJ no haystack pra
              // bater com busca crua (ex: "57571275000607") alem da formatada.
              searchValue: `${h.nome_fantasia || ''} ${h.razao_social || ''} ${(h.cnpj || '').replace(/\D/g, '')} ${h.cnpj || ''}`
            }))}
            value={selectedHospitalId}
            onChange={handleSelectHospital}
            placeholder="Selecione um hospital..."
            searchPlaceholder="Buscar por nome ou CNPJ..."
            disabled={isReadOnly || loadingHospitais}
            isLoading={loadingHospitais}
          />
        </div>
      </div>

      {/* Locais Planejados - Epic LOCAIS-POR-CLIENTE: grouped by cliente */}
      {locaisPorClienteHook ? (
        <AgendamentoLocaisPorClienteSection
          selectedHospitalId={selectedHospitalId}
          selectedClienteIds={selectedClienteIds}
          clientesDisponiveis={clientesDisponiveis}
          previewData={locaisPorClienteHook.previewData}
          locaisPorCliente={locaisPorClienteHook.locaisPorCliente}
          loadingPreview={locaisPorClienteHook.loadingPreview}
          errorPreview={locaisPorClienteHook.errorPreview}
          toggleLocal={locaisPorClienteHook.toggleLocal}
          adicionarLocalExtra={locaisPorClienteHook.adicionarLocalExtra}
          removerLocal={locaisPorClienteHook.removerLocal}
          isLocalShared={locaisPorClienteHook.isLocalShared}
          isReadOnly={isReadOnly}
          totalLocaisSelecionados={locaisPorClienteHook.totalLocaisSelecionados}
          locaisContagemDisponiveis={locaisContagemDisponiveis}
        />
      ) : (
        <AgendamentoLocaisSection
          selectedHospitalId={selectedHospitalId}
          loadingLocais={loadingLocais}
          locaisContagemDisponiveis={locaisContagemDisponiveis}
          locaisContagemSelecionados={locaisContagemSelecionados}
          selectedLocalId={selectedLocalId}
          setSelectedLocalId={setSelectedLocalId}
          handleAddLocal={handleAddLocal}
          handleRemoveLocal={handleRemoveLocal}
          isReadOnly={isReadOnly}
        />
      )}

      {/* N103 (Alana): endereço + telefones + e-mails dos responsáveis atrás de um
          botão, fechado por padrão. O N75 mostrava a lista de e-mails inline e
          sempre aberta, e foi isso que ela pediu para não poluir a tela. */}
      <HospitalContatosSection
        hospital={hospitaisDisponiveis.find((h) => h.id === selectedHospitalId)}
        clientes={clientesDisponiveis.filter((c) => selectedClienteIds.includes(c.id))}
        locais={locaisContagemSelecionados}
      />

      {/* Auditores e Observações */}
      <div className="grid grid-cols-2 gap-6">
        {/* COLUNA ESQUERDA - Gerenciar Auditor */}
        <AgendamentoAuditoresSection
          auditoresDisponiveis={auditoresDisponiveis}
          loadingAuditores={loadingAuditores}
          errorAuditores={errorAuditores}
          auditoresParticipantes={auditoresParticipantes}
          selectedAuditorId={selectedAuditorId}
          setSelectedAuditorId={setSelectedAuditorId}
          handleAddAuditor={handleAddAuditor}
          handleRemoveAuditor={handleRemoveAuditor}
          isReadOnly={isReadOnly}
          modifiedFields={modifiedFields}
        />

        {/* COLUNA DIREITA - Observações + Motivo do Cancelamento */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Observações do Agendamento
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleChange('observacoes', e.target.value)}
              rows={3}
              disabled={isReadOnly}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-60 disabled:cursor-not-allowed ${
                modifiedFields.has('observacoes')
                  ? 'border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50'
                  : 'border-gray-200 dark:border-gray-600'
              }`}
              placeholder="Digite as observações..."
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {isNegativeStatus && (
                <span className="text-red-500 animate-pulse" title="Campo obrigatório">⚠️</span>
              )}
              Motivo do Cancelamento ou negativa
              {isNegativeStatus && (
                <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full font-medium ml-2">
                  Obrigatório
                </span>
              )}
            </label>

            <select
              value={formData.motivoCancelamento}
              onChange={(e) => handleChange('motivoCancelamento', e.target.value)}
              disabled={isReadOnly || !isNegativeStatus}
              className={`
                w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none
                ${isNegativeStatus && !isReadOnly
                  ? 'border-2 border-red-400 dark:border-red-600 ring-2 ring-red-200 dark:ring-red-900/50 bg-red-50/30 dark:bg-red-900/10 text-gray-900 dark:text-white focus:ring-red-300'
                  : 'border-2 border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-500 cursor-not-allowed opacity-60'
                }
                ${modifiedFields.has('motivoCancelamento') && isNegativeStatus && !isReadOnly
                  ? 'border-orange-400 dark:border-orange-500 ring-orange-200 dark:ring-orange-900/50'
                  : ''
                }
              `}
            >
              <option value="">Selecione um motivo...</option>
              {motivosOptions.map((m: any) => (
                <option key={m.id} value={m.motivo}>{m.motivo}</option>
              ))}
            </select>

            {isNegativeStatus && !isReadOnly && !formData.motivoCancelamento.trim() && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1 animate-fade-in">
                <span>⚠️</span>
                Este campo é obrigatório para status: Não Autorizada, Não Executada ou Cancelada
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
